import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import ChartModal from './ChartModal';
import FuturesStatusPanel from './FuturesStatusPanel';
import { useAuth } from '../context/AuthContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { getPositionSideLabel } from '../utils/positions';


interface SymbolStatus {
  symbol: string;
  last_update: string;
  status: string;
  current_price: number | null;
  current_volume: number | null;
  opening_range_high: number | null;
  opening_range_low: number | null;
  volume_average: number | null;
  volume_ratio: number | null;
  volume_sufficient: boolean | null;
  price_position: string;
  range_established: boolean;
  processing: boolean;
  trade_capped?: boolean;  // True if daily trade cap reached or already traded today
}

interface MarketContext {
  timestamp: string;
  spy_status: string;
  spy_change: string;
  qqq_status: string;
  qqq_change: string;
}

interface ErrorAlert {
  timestamp: string;
  symbol: string | null;
  message: string;
  raw_message: string;
}

interface ORBMonitoringData {
  date: string;
  symbols: SymbolStatus[];
  market_context: MarketContext | null;
  errors: ErrorAlert[];
  last_updated: string;
  total_symbols: number;
  errors_count: number;
}

interface ORBMonitoringProps {
  instanceName: string;
}

const ORBMonitoring: React.FC<ORBMonitoringProps> = ({ instanceName }) => {
  const { token } = useAuth();
  const [data, setData] = useState<ORBMonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  
  // Positions state
  const [positionsData, setPositionsData] = useState<any[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState<string | null>(null);

  // Map internal instance names to display names
  const displayNameMap: { [key: string]: string } = {
    'V1': 'OPTIONS',
    'E_OPTIONS': 'E-OPTIONS',
    'OPTIONS_PCS': 'OPTIONS PCS',
    'TOPORB': 'TIER2',
    'LIVE': 'LIVE',
    'GROK': 'GROK',
    'TIER3': 'TIER3',
    'FUTURES': 'Futures'
  };
  const displayName = displayNameMap[instanceName] || instanceName;

  const fetchData = React.useCallback(async () => {
    if (!token) return; // Don't fetch without token
    try {
      const response = await fetch(`/api/instances/${instanceName}/orb-monitoring`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [instanceName, token]);

  // Fetch positions data from Alpaca API
  const fetchPositionsData = React.useCallback(async () => {
    if (!token) return; // Don't fetch without token
    setPositionsLoading(true);
    setPositionsError(null);
    
    try {
      const response = await fetch(`/api/instances/${instanceName}/positions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      setPositionsData(data.positions || []);
    } catch (error) {
      console.error('Error fetching positions data:', error);
      setPositionsError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setPositionsLoading(false);
    }
  }, [instanceName, token]);

  useEffect(() => {
    if (!token) return; // Wait for token to be available
    fetchData();
    fetchPositionsData();
  }, [fetchData, fetchPositionsData, token]);

  useEffect(() => {
    if (!autoRefresh || !token) return; // Wait for token to be available
    
    const interval = setInterval(() => {
      fetchData();
      fetchPositionsData();
    }, 10000); // Refresh every 10 seconds for real-time monitoring
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData, fetchPositionsData, token]);

  // Chart modal handlers
  const handleSymbolClick = (symbol: string) => {
    setSelectedSymbol(symbol);
    setChartModalOpen(true);
  };

  const handleCloseChartModal = () => {
    setChartModalOpen(false);
    setSelectedSymbol('');
  };

  const formatTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return timestamp.split(' ')[1]?.split(',')[0] || timestamp;
  };

  const formatNumber = (num: number | null) => {
    if (num === null) return 'N/A';
    return num.toLocaleString();
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return 'N/A';
    return `$${price.toFixed(2)}`;
  };

  const getPricePositionIcon = (position: string) => {
    switch (position) {
      case 'above': return <TrendingUpIcon color="success" />;
      case 'below': return <TrendingDownIcon color="error" />;
      case 'within': return <TrendingFlatIcon color="primary" />;
      default: return <TrendingFlatIcon color="disabled" />;
    }
  };

  const getPricePositionColor = (position: string) => {
    switch (position) {
      case 'above': return 'success';
      case 'below': return 'error';
      case 'within': return 'primary';
      default: return 'default';
    }
  };

  const getVolumeColor = (sufficient: boolean | null) => {
    if (sufficient === null) return 'default';
    return sufficient ? 'success' : 'warning';
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'monitoring': return 'success';
      case 'processing': return 'info';
      case 'range_established': return 'primary';
      case 'trade_capped': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="h6">Error loading monitoring data</Typography>
        <Typography variant="body2">{error}</Typography>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        <Typography variant="body1">No monitoring data available</Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{
      '& @keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
      }
    }}>
      {/* Header with refresh and market context */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📊 {displayName} Real-Time Monitoring
            {instanceName !== 'V1' && instanceName !== 'FUTURES' && (
              <Chip 
                label={`${data.total_symbols} symbols`} 
                size="small" 
                color="primary" 
              />
            )}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Last updated: {formatTime(data.last_updated)}
            </Typography>
            <Chip 
              label="Auto-refresh: 10s" 
              size="small" 
              color="success" 
              variant="outlined"
            />
            <Tooltip title={loading ? "Refreshing..." : "Manual refresh"}>
              <IconButton onClick={() => { fetchData(); fetchPositionsData(); }} size="small" disabled={loading}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Market Context */}
        {data.market_context && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip 
              label={`SPY: ${data.market_context.spy_status} (${data.market_context.spy_change})`}
              color={data.market_context.spy_status === 'NEUTRAL' ? 'default' : 
                     data.market_context.spy_change.startsWith('+') ? 'success' : 'error'}
              size="small"
            />
            <Chip 
              label={`QQQ: ${data.market_context.qqq_status} (${data.market_context.qqq_change})`}
              color={data.market_context.qqq_status === 'NEUTRAL' ? 'default' : 
                     data.market_context.qqq_change.startsWith('+') ? 'success' : 'error'}
              size="small"
            />
          </Box>
        )}


      </Paper>

      {/* FUTURES: the bot's own status file replaces log scraping (Docs/DASHBOARD.md §3.4) */}
      {instanceName === 'FUTURES' && <FuturesStatusPanel />}

      {/* Current Positions Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Current Positions ({positionsData.length}):
        </Typography>
        
        {positionsLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading positions...</Typography>
          </Box>
        )}
        
        {positionsError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {positionsError}
          </Alert>
        )}
        
        {positionsData.length === 0 && !positionsLoading && !positionsError && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No open positions
          </Typography>
        )}
        
        {positionsData.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {positionsData.map((position, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 1,
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.12)'
              }}>
                <Chip 
                  label={position.symbol} 
                  color="primary" 
                  size="small"
                  onClick={() => handleSymbolClick(position.symbol)}
                  sx={{ cursor: 'pointer', minWidth: 60 }}
                />
                <Chip 
                  label={getPositionSideLabel(position.symbol, position.side)} 
                  color={position.side === 'long' ? 'success' : 'error'} 
                  size="small"
                  sx={{ minWidth: 60 }}
                />
                <Typography variant="body2" sx={{ 
                  color: position.unrealized_pl >= 0 ? '#2e7d32' : '#d32f2f',
                  fontWeight: 'bold',
                  minWidth: 100
                }}>
                  {position.unrealized_pl >= 0 ? '+' : ''}${position.unrealized_pl.toFixed(2)} 
                  ({position.unrealized_plpc >= 0 ? '+' : ''}{(position.unrealized_plpc * 100).toFixed(2)}%)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {position.quantity ?? position.qty} {instanceName === 'FUTURES' ? 'contracts' : 'shares'} @ ${position.current_price.toFixed(2)}
                </Typography>
                {position.entry_time && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Entry: {position.entry_time}
                  </Typography>
                )}
                {position.stop_loss_percent != null && (
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.75rem',
                    color: '#ff9800',
                    fontWeight: 'bold'
                  }}>
                    Stop: -{position.stop_loss_percent.toFixed(2)}%
                    {position.stop_loss_price && (
                      <span style={{ color: '#ffa726' }}>
                        {' @ $'}{position.stop_loss_price.toFixed(2)}
                      </span>
                    )}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* OPTIONS (V1) runs OPT-SPY-DAILY on its server — there are no
          ORB-format symbols to parse, so the symbols table is hidden. The
          trade status lives in the Live Trade tab and the raw tail in the
          Logs tab (operator, 10/22 — no duplicates here). */}
      {instanceName === 'V1' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
          This instance runs OPT-SPY-DAILY — no ORB symbols to monitor. Trade status: the Live Trade tab. Raw log: the Logs tab.
        </Typography>
      )}

      {/* Symbols Monitoring Table (hidden for V1 — no ORB symbols by design — and for
          FUTURES, whose [ORB] lines this parser can't read; the status panel above replaces it) */}
      {instanceName !== 'V1' && instanceName !== 'FUTURES' && (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Symbol</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Current Price</strong></TableCell>
              <TableCell><strong>Opening Range</strong></TableCell>
              <TableCell><strong>Position</strong></TableCell>
              <TableCell><strong>Volume</strong></TableCell>
              <TableCell><strong>Volume Ratio</strong></TableCell>
              <TableCell><strong>Last Update</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.symbols.map((symbol, index) => {
              const rowKey = `${symbol.symbol}-${index}`;

              // Check if this symbol is currently in a position
              const isInPosition = positionsData.some(pos => pos.symbol === symbol.symbol);

              // Check if this symbol has hit its trade cap
              const isTradeCapped = symbol.trade_capped === true;

              // Determine row background color
              const getRowStyle = () => {
                if (isInPosition) {
                  // Active position - light green/cyan tint
                  return {
                    backgroundColor: 'rgba(0, 200, 150, 0.15)',
                    '&:hover': { backgroundColor: 'rgba(0, 200, 150, 0.25)' }
                  };
                }
                if (isTradeCapped) {
                  // Trade capped - greyed out
                  return {
                    backgroundColor: 'rgba(128, 128, 128, 0.2)',
                    opacity: 0.6,
                    '&:hover': { backgroundColor: 'rgba(128, 128, 128, 0.3)' }
                  };
                }
                return {};
              };
              
              return (
              <TableRow key={rowKey} hover sx={getRowStyle()}>
                <TableCell>
                  <Typography
                    variant="body1"
                    onClick={() => handleSymbolClick(symbol.symbol)}
                    sx={{ 
                      fontWeight: 'bold',
                      color: '#90caf9', 
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {symbol.symbol}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={symbol.status}
                    color={getStatusColor(symbol.status)}
                    size="small"
                  />
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {formatPrice(symbol.current_price)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  {symbol.range_established ? (
                    <Box>
                      <Typography variant="body2" color="success.main">
                        {formatPrice(symbol.opening_range_low)} - {formatPrice(symbol.opening_range_high)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      Not established
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getPricePositionIcon(symbol.price_position)}
                    <Chip 
                      label={symbol.price_position} 
                      color={getPricePositionColor(symbol.price_position)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatNumber(symbol.current_volume)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      avg: {formatNumber(symbol.volume_average)}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {symbol.volume_sufficient ? <VolumeUpIcon color="success" fontSize="small" /> : 
                     <VolumeOffIcon color="warning" fontSize="small" />}
                    <Chip 
                      label={symbol.volume_ratio !== null && symbol.volume_ratio !== undefined ? `${symbol.volume_ratio.toFixed(2)}x` : 'N/A'}
                      color={getVolumeColor(symbol.volume_sufficient)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  {symbol.volume_ratio !== null && symbol.volume_ratio !== undefined && (
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(symbol.volume_ratio * 100 / 1.5, 100)} 
                      sx={{ mt: 0.5, height: 4 }}
                      color={symbol.volume_sufficient ? 'success' : 'warning'}
                    />
                  )}
                </TableCell>
                
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(symbol.last_update)}
                  </Typography>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Chart Modal */}
      <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChartModal}
        symbol={selectedSymbol}
      />
    </Box>
  );
};

export default ORBMonitoring;